import { formatBytes } from '@/utils/formatBytes';
import { getFileTypeLabel } from '@/utils/fileValidation';

interface StagedFileCardProps {
  file: File;
  pageCount: number | null;
  isDetecting: boolean;
  onReplace: () => void;
  onRemove: () => void;
}

export function StagedFileCard({ file, pageCount, isDetecting, onReplace, onRemove }: StagedFileCardProps) {
  const typeLabel = getFileTypeLabel(file.type);
  const isPdf = typeLabel === 'PDF';

  return (
    <section className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="text-label-md text-on-surface-variant">Staged for Print</span>
        {!isDetecting && pageCount !== null && (
          <span className="text-label-sm text-success d-flex align-items-center gap-1">
            <i className="bi bi-patch-check-fill" />
            Validated
          </span>
        )}
      </div>

      <div className="bg-surface-lowest rounded-4 shadow-kiosk-md p-3">
        <div className="d-flex align-items-start gap-3">
          <div
            className="rounded-3 bg-surface-container d-flex align-items-center justify-content-center flex-shrink-0 position-relative overflow-hidden"
            style={{ width: 64, height: 80 }}
          >
            <i
              className={`bi ${isPdf ? 'bi-file-earmark-pdf' : 'bi-file-earmark-text'} text-primary`}
              style={{ fontSize: 28 }}
            />
            <span
              className="position-absolute bottom-0 start-0 end-0 bg-primary text-white text-center py-1 fw-bold"
              style={{ fontSize: 9, letterSpacing: '0.05em' }}
            >
              {typeLabel}
            </span>
          </div>

          <div className="flex-grow-1 min-w-0">
            <h4
              className="text-label-lg text-on-surface mb-1"
              title={file.name}
              style={{
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {file.name}
            </h4>

            {isDetecting ? (
              <span className="text-body-sm text-on-surface-variant d-flex align-items-center gap-2">
                <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
                Counting pages…
              </span>
            ) : (
              <div className="d-flex flex-wrap gap-1">
                <span className="badge bg-surface-container text-on-surface-variant fw-medium">
                  {pageCount !== null ? `${pageCount} Pages` : 'Pages — enter on next step'}
                </span>
                <span className="badge bg-surface-container text-on-surface-variant fw-medium">
                  {formatBytes(file.size)}
                </span>
              </div>
            )}
          </div>
        </div>

        {!isDetecting && (
          <div className="d-flex align-items-center justify-content-between pt-3 mt-3 border-top">
            <button
              type="button"
              onClick={onReplace}
              className="btn text-primary d-flex align-items-center gap-2 text-label-md active-press border-0 px-2"
            >
              <i className="bi bi-arrow-repeat" />
              <span>Replace File</span>
            </button>
            <button
              type="button"
              onClick={onRemove}
              aria-label="Remove document"
              className="btn text-danger d-flex align-items-center justify-content-center tap-target active-press border-0"
            >
              <i className="bi bi-trash" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
